import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Users, Plus, Search, Loader2, Phone, Mail, MapPin,
  Calendar, ChevronRight, Trash2, Edit2, Baby, X,
  User, FileText, ArrowLeft
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import { confirmToast } from '@/utils/confirmToast';
import Sidebar from '@/components/Sidebar';

export default function ClientsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddClient, setShowAddClient] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const emptyClient = {
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: 'CA',
      zip: ''
    },
    children: []
  };
  
  const [clientForm, setClientForm] = useState(emptyClient);
  const [newChild, setNewChild] = useState({ full_name: '', date_of_birth: '', gender: '' });
  const [showAddChild, setShowAddChild] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);

  useEffect(() => {
    const initData = async () => {
      await fetchUser();
      await fetchClients();
      await fetchStateOptions();
    };
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        if (userData.app_mode !== 'crm') {
          toast.error('CRM features require CRM subscription');
          navigate('/dashboard');
        }
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchClients = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };


  const fetchStateOptions = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/global-fields/option-lists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const usaStatesList = data.system_lists.find(list => list.name === 'UsaStates');
        if (usaStatesList) {
          setStateOptions(usaStatesList.options);
        }
      }
    } catch (error) {
      console.error('Failed to load state options');
    }
  };

  const handleSaveClient = async () => {
    if (!clientForm.full_name.trim()) {
      toast.error('Client name is required');
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const url = editingClient 
        ? `${API}/crm/clients/${editingClient.client_id}`
        : `${API}/crm/clients`;
      
      const response = await fetch(url, {
        method: editingClient ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(clientForm)
      });
      
      if (response.ok) {
        toast.success(editingClient ? 'Client updated' : 'Client created');
        setShowAddClient(false);
        setEditingClient(null);
        setClientForm(emptyClient);
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save client');
      }
    } catch (error) {
      toast.error('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    const clientName = client?.full_name || 'this client';
    
    confirmToast(
      `Are you sure you want to delete "${clientName}"? This action cannot be undone.`,
      async () => {
        const token = localStorage.getItem('token');
        
        try {
          const response = await fetch(`${API}/crm/clients/${clientId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            toast.success('Client deleted successfully');
            fetchClients();
          } else {
            const error = await response.json();
            toast.error(error.detail || 'Failed to delete client');
          }
        } catch (error) {
          toast.error('Failed to delete client');
        }
      },
      { title: 'Delete Client' }
    );
  };

  const handleEditClient = (client) => {
    navigate(`/clients/${client.client_id}/edit`);
  };

  const handleAddChild = () => {
    if (!newChild.full_name.trim()) {
      toast.error('Child name is required');
      return;
    }
    
    const childWithId = {
      ...newChild,
      child_id: `ch_temp_${Date.now()}`
    };
    
    setClientForm({
      ...clientForm,
      children: [...clientForm.children, childWithId]
    });
    
    setNewChild({ full_name: '', date_of_birth: '', gender: '' });
    setShowAddChild(false);
  };

  const handleRemoveChild = (childId) => {
    setClientForm({
      ...clientForm,
      children: clientForm.children.filter(c => c.child_id !== childId)
    });
  };

  const filteredClients = clients.filter(client =>
    client.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.phone?.includes(searchQuery)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8 lg:pt-8 pt-20">{/* pt-20 for mobile header */}
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Clients</h1>
            <p className="text-slate-500">{clients.length} client{clients.length !== 1 ? 's' : ''}</p>
          </div>
          <Button
            onClick={() => navigate('/clients/new')}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search clients by name, email, or phone..."
            className="pl-12 h-12 rounded-xl bg-white"
            data-testid="search-clients"
          />
        </div>

        {/* Client List - Table */}
        {filteredClients.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {searchQuery ? 'No clients found' : 'No clients yet'}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchQuery 
                ? 'Try a different search term' 
                : 'Add your first client to get started'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => setShowAddClient(true)}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Client
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="clients-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Name</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Contact</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Location</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Children</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Added</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredClients.map((client) => (
                    <tr
                      key={client.client_id}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`client-row-${client.client_id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center text-white font-semibold text-sm">
                            {client.full_name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{client.full_name}</p>
                            {client.date_of_birth && (
                              <p className="text-xs text-slate-500">
                                DOB: {new Date(client.date_of_birth).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {client.email && (
                            <p className="text-sm text-slate-600 flex items-center gap-2">
                              <Mail className="w-4 h-4 text-slate-400" />
                              {client.email}
                            </p>
                          )}
                          {client.phone && (
                            <p className="text-sm text-slate-600 flex items-center gap-2">
                              <Phone className="w-4 h-4 text-slate-400" />
                              {client.phone}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {client.address?.city ? (
                          <p className="text-sm text-slate-600 flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            {client.address.city}, {client.address.state}
                          </p>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {client.children?.length > 0 ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-medium">
                            <Baby className="w-3 h-3" />
                            {client.children.length}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-slate-600">
                          {new Date(client.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClient(client)}
                            className="rounded-lg h-8 w-8 p-0 hover:bg-violet-50 hover:text-violet-700"
                            data-testid={`edit-client-btn-${client.client_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClient(client.client_id)}
                            className="rounded-lg h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-client-btn-${client.client_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Add/Edit Client Modal */}
      {showAddClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-xl font-semibold">
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowAddClient(false); setEditingClient(null); }}
                className="rounded-lg"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Basic Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-slate-600">Full Name *</Label>
                    <Input
                      value={clientForm.full_name}
                      onChange={(e) => setClientForm({ ...clientForm, full_name: e.target.value })}
                      placeholder="John Doe"
                      className="mt-1"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">Email</Label>
                      <Input
                        value={clientForm.email}
                        onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                        placeholder="john@example.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Phone</Label>
                      <Input
                        value={clientForm.phone}
                        onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Date of Birth</Label>
                    <Input
                      type="date"
                      value={clientForm.date_of_birth}
                      onChange={(e) => setClientForm({ ...clientForm, date_of_birth: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </h3>
                <div className="space-y-3">
                  <Input
                    value={clientForm.address.address_line_1}
                    onChange={(e) => setClientForm({ 
                      ...clientForm, 
                      address: { ...clientForm.address, address_line_1: e.target.value }
                    })}
                    placeholder="Address Line 1"
                  />
                  <Input
                    value={clientForm.address.address_line_2}
                    onChange={(e) => setClientForm({ 
                      ...clientForm, 
                      address: { ...clientForm.address, address_line_2: e.target.value }
                    })}
                    placeholder="Address Line 2 (Apt, Suite, etc.)"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      value={clientForm.address.city}
                      onChange={(e) => setClientForm({ 
                        ...clientForm, 
                        address: { ...clientForm.address, city: e.target.value }
                      })}
                      placeholder="City"
                    />
                    <select
                      value={clientForm.address.state}
                      onChange={(e) => setClientForm({ 
                        ...clientForm, 
                        address: { ...clientForm.address, state: e.target.value }
                      })}
                      className="h-11 rounded-lg border border-slate-200 px-3 text-sm"
                    >
                      <option value="">Select State</option>
                      {stateOptions.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    <Input
                      value={clientForm.address.zip}
                      onChange={(e) => setClientForm({ 
                        ...clientForm, 
                        address: { ...clientForm.address, zip: e.target.value }
                      })}
                      placeholder="ZIP"
                    />
                  </div>
                </div>
              </div>

              {/* Children */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Baby className="w-4 h-4" />
                    Children ({clientForm.children.length})
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddChild(true)}
                    className="rounded-lg text-xs"
                    data-testid="open-add-child-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Child
                  </Button>
                </div>
                
                {showAddChild && (
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 mb-3">
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <Label className="text-xs text-slate-600">Name *</Label>
                        <Input
                          value={newChild.full_name}
                          onChange={(e) => setNewChild({ ...newChild, full_name: e.target.value })}
                          placeholder="Child's name"
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Date of Birth</Label>
                        <Input
                          type="date"
                          value={newChild.date_of_birth}
                          onChange={(e) => setNewChild({ ...newChild, date_of_birth: e.target.value })}
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Gender</Label>
                        <select
                          value={newChild.gender}
                          onChange={(e) => setNewChild({ ...newChild, gender: e.target.value })}
                          className="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-sm"
                        >
                          <option value="">Select</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleAddChild} 
                        size="sm" 
                        className="rounded-lg bg-violet-600 hover:bg-violet-700 text-white"
                        data-testid="add-child-submit-btn"
                      >
                        Add
                      </Button>
                      <Button onClick={() => setShowAddChild(false)} size="sm" variant="outline" className="rounded-lg">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
                
                {clientForm.children.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No children added</p>
                ) : (
                  <div className="space-y-2">
                    {clientForm.children.map((child) => (
                      <div key={child.child_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <div>
                          <span className="font-medium text-sm">{child.full_name}</span>
                          {child.date_of_birth && (
                            <span className="text-xs text-slate-500 ml-2">
                              DOB: {new Date(child.date_of_birth).toLocaleDateString()}
                            </span>
                          )}
                          {child.gender && (
                            <span className="text-xs text-slate-500 ml-2">• {child.gender}</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveChild(child.child_id)}
                          className="rounded-lg h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white rounded-b-2xl">
              <Button
                variant="outline"
                onClick={() => { setShowAddClient(false); setEditingClient(null); }}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveClient}
                disabled={saving}
                className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="save-client-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                ) : (
                  editingClient ? 'Update Client' : 'Create Client'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
